import { Button, ErrorAlert, Notice, TextInput } from '@lumlab/platform-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { authClient } from '../app/auth.ts';
import { AuthScreen, authErrorCode } from '../app/AuthScreen.tsx';

export function TwoFactorPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const session = authClient.useSession();
    const [backup, setBackup] = useState(false);
    const [code, setCode] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<unknown>(null);
    return (
        <AuthScreen title={t('platform.security.challengeTitle')}>
            {session.data ? (
                <Notice tone="info">{t('platform.security.reconnect')}</Notice>
            ) : (
                <form
                    className="flex flex-col gap-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        setPending(true);
                        setError(null);
                        void (async () => {
                            try {
                                const result = backup
                                    ? await authClient.twoFactor.verifyBackupCode({ code: code.trim() })
                                    : await authClient.twoFactor.verifyTotp({ code: code.trim() });
                                if (result.error) setError({ code: authErrorCode(result.error) });
                                else {
                                    await authClient.getSession({ query: { disableCookieCache: true } });
                                    await queryClient.invalidateQueries({ queryKey: ['account-state'] });
                                    await navigate('/', { replace: true });
                                }
                            } catch {
                                setError({ code: 'internal' });
                            } finally {
                                setPending(false);
                            }
                        })();
                    }}
                >
                    <p className="text-sm text-muted">
                        {backup ? t('platform.security.backupIntro') : t('platform.security.challengeIntro')}
                    </p>
                    {error !== null && <ErrorAlert error={error} />}
                    <TextInput
                        label={backup ? t('platform.security.backupCode') : t('platform.security.code')}
                        autoComplete="one-time-code"
                        inputMode={backup ? 'text' : 'numeric'}
                        required
                        value={code}
                        onChange={(event) => setCode(event.target.value)}
                    />
                    <Button type="submit" loading={pending}>
                        {t('platform.security.verify')}
                    </Button>
                    <Button
                        variant="default"
                        onClick={() => {
                            setBackup(!backup);
                            setCode('');
                            setError(null);
                        }}
                    >
                        {backup ? t('platform.security.useAuthenticator') : t('platform.security.useBackup')}
                    </Button>
                </form>
            )}
            <Button
                variant="ghost"
                onClick={() => {
                    void authClient.signOut().then(async () => {
                        queryClient.clear();
                        await navigate('/sign-in', { replace: true });
                    });
                }}
            >
                {t('platform.auth.backToSignIn')}
            </Button>
        </AuthScreen>
    );
}
